import React, { Fragment, useEffect, useMemo, useState } from "react";
import "./KYC-PrimaryContact.css";
import { Breadcrumb, Button, Card, Spin } from "antd";
import {
	ApartmentOutlined,
	DoubleLeftOutlined,
	EnvironmentOutlined,
	GlobalOutlined,
	MailOutlined,
	PhoneOutlined,
	PushpinOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import KycService from "../../services/kyc-service";
import {
	KYCAccountNameImage,
	KYCContactsImage,
	KYCCylindersImage,
	KYCDesignImage,
	KYCMaterialsImage,
} from "../Elite/imageAssets";

const defaultPayload = {
	fromDate: "2026-04-09T12:10:21.201Z",
	toDate: "2026-04-09T12:10:21.201Z",
	distributionChannel: "",
	priceGroup: "",
	salesDistrict: "",
	salesOffice: "",
	country: "",
	director: "",
	generalManager: "",
	continentalManager: "",
	regionalManager: "",
	areaManager: "",
	customerGroup: "",
	materialGroup: "",
	materialType: "",
	materialDivision: "",
	materialStructure: "",
	materialCharacteristics: "",
	materialHierarchy1: "",
	materialHierarchy2: "",
	materialHierarchy3: "",
};

const getValue = (obj, keys) => {
	for (const key of keys) {
		const value = obj?.[key];
		if (
			value !== undefined &&
			value !== null &&
			value !== "" &&
			value !== "Microsoft.Xrm.Sdk.OptionSetValue"
		) {
			return value;
		}
	}

	return "-";
};

const getFirstAvailableValue = (...values) =>
	values.find((value) => value !== undefined && value !== null && value !== "");

const buildAddressLine = (data) => {
	const parts = [
		getValue(data, ["address1_Line1", "address1_line1", "address1"]),
		getValue(data, ["address1_Line2", "address1_line2", "address2"]),
		getValue(data, ["address1_Line3", "address1_county", "area", "areaName"]),
		getValue(data, ["ispl_City", "address1_city", "city"]),
	]
		.filter((part) => part && part !== "-");

	return parts.length ? parts.join(", ") : "-";
};

const KycPrimaryContactDetails = () => {
	const { sapCode } = useParams();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const [isCardsLoading, setIsCardsLoading] = useState(false);
	const [primaryContactData, setPrimaryContactData] = useState({});
	const [accountNameCount, setAccountNameCount] = useState({});
	const [contactsCount, setContactsCount] = useState({});
	const [materialsCount, setMaterialsCount] = useState({});
	const [designsCount, setDesignsCount] = useState({});
	const [cylindersCount, setCylindersCount] = useState({});

	const fromAccountTab = Boolean(location.state?.fromAccountTab);
	const groupCompanyBreadcrumbLabel = location.state?.groupCompanyName || "Group Company";
	const accountBreadcrumbLabel = location.state?.accountName || "Account";

	const getCardTitle = (countObj, label, keys = ["total"]) => {
		const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
		return countValue !== undefined ? `${countValue} ${label}` : label;
	};

	useEffect(() => {
		if (!sapCode) {
			return;
		}

		setIsLoading(true);
		KycService.getPrimaryContactDataBySapCodeAPI(sapCode)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data?.responseData;
					const finalData = Array.isArray(responseData) ? responseData[0] || {} : responseData || {};
					setPrimaryContactData(finalData);
				} else {
					NotificationManager.error(response.data?.message || "Unable to load primary contact details.");
					setPrimaryContactData({});
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load primary contact details.");
				setPrimaryContactData({});
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [sapCode]);

	useEffect(() => {
		setIsCardsLoading(true);
		KycService.getKycTileCountsAPI(defaultPayload)
			.then((response) => {
				if (response.data?.statusCode === 200) {
					const apiData = response.data.responseData || {};
					setAccountNameCount(apiData.company ? apiData.company : {});
					setContactsCount(apiData.contacts ? apiData.contacts : {});
					setMaterialsCount(apiData.materials ? apiData.materials : {});
					setDesignsCount(apiData.designs ? apiData.designs : {});
					setCylindersCount(apiData.cylinders ? apiData.cylinders : {});
				} else {
					NotificationManager.error(response.data?.message || "Unable to load KYC tile counts.");
				}
			})
			.catch((error) => {
				console.log(error, "Error");
			})
			.finally(() => {
				setIsCardsLoading(false);
			});
	}, []);

	const contactDisplay = useMemo(() => {
		const firstName = getValue(primaryContactData, [
			"firstName",
			"firstname",
			"ispl_FirstName",
		]);
		const lastName = getValue(primaryContactData, [
			"lastName",
			"lastname",
			"ispl_LastName",
		]);
		const fullName =
			firstName !== "-" && lastName !== "-"
				? `${firstName} ${lastName}`.trim()
				: firstName !== "-"
					? firstName
					: lastName !== "-"
						? lastName
						: "-";

		const avatarInitial =
			fullName !== "-"
				? fullName.trim().charAt(0).toUpperCase()
				: "?";

		return {
			displayName: fullName !== "-" ? fullName.toUpperCase() : "-",
			avatarInitial,
			email: getValue(primaryContactData, ["email", "emailaddress1", "primaryEmail"]),
			phone: getValue(primaryContactData, [
				"mobilePhone",
				"mobilephone",
				"telephone1",
				"phone",
			]),
			addressLine: buildAddressLine(primaryContactData),
			geoItems: [
				{
					key: "country",
					label: "Country",
					value: getValue(primaryContactData, [
						"ispl_Country",
						"address1_country",
						"country",
						"countryName",
					]),
					icon: <GlobalOutlined />,
				},
				{
					key: "area",
					label: "Area",
					value: getValue(primaryContactData, [
						"address1_Line3",
						"address1_county",
						"area",
						"areaName",
						"ispl_Area",
					]),
					icon: <PushpinOutlined />,
				},
				{
					key: "state",
					label: "State",
					value: getValue(primaryContactData, [
						"ispl_State",
						"address1_stateorprovince",
						"state",
						"stateName",
					]),
					icon: <EnvironmentOutlined />,
				},
				{
					key: "postalCode",
					label: "Postal Code",
					value: getValue(primaryContactData, [
						"ispl_PostCode",
						"address1_postalcode",
						"postalCode",
						"zipcode",
					]),
					icon: <ApartmentOutlined />,
				},
			],
		};
	}, [primaryContactData]);

	return (
		<Fragment>
			<div className="dashboard app-content content">
				<div className="content-wrapper">
					<div className="content-body">
						<div className="d-flex justify-content-between align-items-center details_view_title mb-2">
							<Breadcrumb className="details_breadcrum" separator=">">
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								<Breadcrumb.Item>
									<Link to="/kyc?companyTab=groupCompany">
										<label>Group Company</label>
										{/* <label>{groupCompanyBreadcrumbLabel}</label> */}
									</Link>
								</Breadcrumb.Item>
								{fromAccountTab ? (
									<Breadcrumb.Item>
										<Link to="/kyc?companyTab=account">
											<label>Account</label>
										</Link>
										{/* <label>{accountBreadcrumbLabel}</label> */}
									</Breadcrumb.Item>
								) : null}
								<Breadcrumb.Item>
									<label>Primary Contact</label>
								</Breadcrumb.Item>
							</Breadcrumb>
							<Link to="/kyc">
								<Button type="primary back_btn" icon={<DoubleLeftOutlined />}>
									Back
								</Button>
							</Link>
						</div>
						<Spin size="large" spinning={isLoading || isCardsLoading}>
							<div className="row">
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_yellow">
										<div className="card-content">
											<div className="kyc-card-body KYCAccountName images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCAccountNameImage} alt="Img" />
														<span>
															{getCardTitle(accountNameCount, "Account Name", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															-
														</h5>
														<h6 className="text-white mb-0">Ship to Party</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															-
														</h5>
														<h6 className="text-white mb-0">Bill to Party</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															-
														</h5>
														<h6 className="text-white mb-0">Payer</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_red">
										<div className="card-content">
											<div className="kyc-card-body KYCContacts images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCContactsImage} alt="Img" />
														<span>
															{getCardTitle(contactsCount, "Contacts", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(contactsCount).length > 0
																? contactsCount.running
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(contactsCount).length > 0
																? contactsCount.hide
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_blue">
										<div className="card-content">
											<div className="kyc-card-body KYCMaterials images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCMaterialsImage} alt="Img" />
														<span>
															{getCardTitle(materialsCount, "Materials", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(materialsCount).length > 0
																? materialsCount.running
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(materialsCount).length > 0
																? materialsCount.hide
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_grey">
										<div className="card-content">
											<div className="kyc-card-body KYCDesign images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCDesignImage} alt="Img" />
														<span>
															{getCardTitle(designsCount, "Designs", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(designsCount).length > 0
																? designsCount.running
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(designsCount).length > 0
																? designsCount.hide
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Hide</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_purple">
										<div className="card-content">
											<div className="kyc-card-body KYCCylinders images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCCylindersImage} alt="Img" />
														<span>
															{getCardTitle(cylindersCount, "Cylinders", [
																"totalCount",
																"total",
															])}
														</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(cylindersCount).length > 0
																? cylindersCount.running
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3"></div>
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{Object.keys(cylindersCount).length > 0
																? cylindersCount.close
																: "-"}
														</h5>
														<h6 className="text-white mb-0">Close</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

							</div>

							<Card className="kyc-primary-details-card">
								<div className="kyc-primary-contact-panel">
									<div className="kyc-primary-contact-panel__head">
										<span className="kyc-primary-contact-panel__head-icon" aria-hidden>
											<UserOutlined />
										</span>
										<h3>Primary Contact Details</h3>
									</div>

									<div className="kyc-primary-contact-profile">
										<div
											className="kyc-primary-contact-avatar"
											aria-hidden={contactDisplay.displayName === "-"}
										>
											{contactDisplay.avatarInitial}
										</div>
										<div className="kyc-primary-contact-profile__body">
											<h4>{contactDisplay.displayName}</h4>
											<div className="kyc-primary-contact-chips">
												<div className="kyc-primary-contact-chip">
													<span
														className="kyc-primary-contact-chip__icon kyc-primary-contact-chip__icon--email"
														aria-hidden
													>
														<MailOutlined />
													</span>
													<span className="kyc-primary-contact-chip__text">
														{contactDisplay.email}
													</span>
												</div>
												<div className="kyc-primary-contact-chip">
													<span
														className="kyc-primary-contact-chip__icon kyc-primary-contact-chip__icon--phone"
														aria-hidden
													>
														<PhoneOutlined />
													</span>
													<span className="kyc-primary-contact-chip__text">
														{contactDisplay.phone}
													</span>
												</div>
											</div>
										</div>
									</div>

									<div className="kyc-primary-contact-section">
										<div className="kyc-primary-contact-section__label">
											<span
												className="kyc-primary-contact-address__icon"
												aria-hidden
											>
												<EnvironmentOutlined />
											</span>
											Address
										</div>
										<div className="kyc-primary-contact-address">
											<span className="kyc-primary-contact-address__icon" aria-hidden>
												<ApartmentOutlined />
											</span>
											<p>{contactDisplay.addressLine}</p>
										</div>
									</div>

									<div className="kyc-primary-contact-geo">
										{contactDisplay.geoItems.map((item) => (
											<div className="kyc-primary-contact-geo-item" key={item.key}>
												<span className="kyc-primary-contact-geo-item__icon" aria-hidden>
													{item.icon}
												</span>
												<div className="kyc-primary-contact-geo-item__content">
													<label>{item.label}</label>
													<p>{item.value}</p>
												</div>
											</div>
										))}
									</div>
								</div>
							</Card>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycPrimaryContactDetails;
